import React, { useState } from "react";
import { PiggyBank, Loader2 } from "lucide-react";
import CalculatorShell from "@/components/tools/CalculatorShell";
import { NumberInput, SliderInput } from "@/components/tools/Inputs";
import ResultsCard from "@/components/tools/ResultsCard";
import { useProducts } from "@/hooks/useProducts";
import { postTool } from "@/lib/api";
import { formatUSD, buildApplyQuery } from "@/lib/format";

export default function RetirementIncome() {
  const { products } = useProducts();
  const [form, setForm] = useState({
    current_age: 40,
    retirement_age: 65,
    current_savings: 50000,
    monthly_contribution: 1000,
    expected_return: 0.05,
    desired_monthly_income: 6000,
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState("");
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (form.current_age < 18 || form.current_age > 90)
      e.current_age = "Age must be 18–90.";
    if (form.retirement_age <= form.current_age)
      e.retirement_age = "Must be after your current age.";
    if (form.current_savings < 0) e.current_savings = "Must be 0 or more.";
    if (form.monthly_contribution < 0)
      e.monthly_contribution = "Must be 0 or more.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setLoading(true);
    setServerError("");
    setResult(null);
    try {
      await new Promise((r) => setTimeout(r, 500));
      const data = await postTool("retirement-income", form);
      setResult(data);
    } catch {
      setServerError("Could not calculate. Please check your inputs.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <CalculatorShell
      title="Retirement Income Calculator"
      blurb="See your projected nest egg and the monthly income it could produce."
      duration="Takes ~45 seconds"
      Icon={PiggyBank}
    >
      <form
        onSubmit={onSubmit}
        data-testid="retirement-form"
        className="bg-white rounded-2xl border border-lw-navy/10 p-6 sm:p-8 space-y-6"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <NumberInput
            label="Current age"
            value={form.current_age}
            onChange={set("current_age")}
            min={18}
            max={90}
            testId="ret-current-age"
            error={errors.current_age}
          />
          <NumberInput
            label="Retirement age"
            value={form.retirement_age}
            onChange={set("retirement_age")}
            min={40}
            max={90}
            testId="ret-retire-age"
            error={errors.retirement_age}
          />
          <NumberInput
            label="Current retirement savings"
            value={form.current_savings}
            onChange={set("current_savings")}
            prefix="$"
            min={0}
            testId="ret-savings"
            error={errors.current_savings}
          />
          <NumberInput
            label="Monthly contribution"
            value={form.monthly_contribution}
            onChange={set("monthly_contribution")}
            prefix="$"
            min={0}
            testId="ret-contrib"
            error={errors.monthly_contribution}
          />
        </div>

        <SliderInput
          label="Expected annual return"
          value={form.expected_return}
          onChange={set("expected_return")}
          min={0.03}
          max={0.08}
          step={0.005}
          formatValue={(v) => `${(v * 100).toFixed(1)}%`}
          testId="ret-return"
        />

        <NumberInput
          label="Desired monthly income in retirement"
          value={form.desired_monthly_income}
          onChange={set("desired_monthly_income")}
          prefix="$"
          min={0}
          testId="ret-desired"
          helper="The lifestyle you'd like to maintain."
        />

        {serverError && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            {serverError}
          </p>
        )}

        <button
          type="submit"
          disabled={loading}
          data-testid="retirement-submit"
          className="btn-gold w-full sm:w-auto disabled:opacity-60"
        >
          {loading ? (
            <>
              <Loader2 className="animate-spin" size={18} /> Calculating…
            </>
          ) : (
            <>Project my retirement →</>
          )}
        </button>
      </form>

      {result && (
        <ResultsCard
          source="retirement-income"
          headline={`~${formatUSD(result.projected_nest_egg)} at age ${form.retirement_age}`}
          subhead={`That's roughly ${formatUSD(result.monthly_income_4pct)}/month at a 4% withdrawal — or ${formatUSD(
            result.monthly_income_annuity,
          )}/month with a guaranteed lifetime annuity.`}
          rows={[
            { label: "Years to retirement", value: `${result.years_to_retirement} yrs`, testId: "years" },
            {
              label: "Projected nest egg",
              value: formatUSD(result.projected_nest_egg),
              testId: "nest-egg",
            },
            {
              label: "Monthly income (4% rule)",
              value: formatUSD(result.monthly_income_4pct),
              testId: "income-4",
            },
            {
              label: "Monthly income (lifetime annuity)",
              value: formatUSD(result.monthly_income_annuity),
              testId: "income-annuity",
            },
            {
              label: "Your desired monthly income",
              value: formatUSD(result.desired_monthly_income),
              testId: "desired",
            },
            {
              label: "Monthly gap vs goal",
              value: formatUSD(result.monthly_income_gap),
              testId: "gap",
            },
          ]}
          assumptions={result.assumptions}
          matchedIds={result.matched_product_ids}
          products={products}
          applyQuery={buildApplyQuery({
            source: "retirement-income",
            current_age: form.current_age,
            retirement_age: form.retirement_age,
            nest_egg: result.projected_nest_egg,
            gap: result.monthly_income_gap,
            products: (result.matched_product_ids || []).join(","),
          })}
          toolSlug="retirement-income"
          toolInputs={form}
          toolResults={result}
        />
      )}
    </CalculatorShell>
  );
}
